import React, { useState, useCallback, useMemo, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
} from '@mui/material'
import { useIntl } from 'react-intl'
import { SprintDetail, SprintStatus } from '../../../../../lib/functions/sprint'
import { UserBasic } from '../../../../../lib/functions/user'
import {
  ProductBacklogItemRow,
  ProductBacklogRow,
  SprintProductItemDetail,
} from '../../productBacklog'
import { WorkloadUnit } from '../../../../../lib/functions/workload'
import { dateV2Service } from '../../../../../domain/value-object/DateV2'
import { useWorkloadUnit } from '../../../../hooks/useWorkloadUnit'
import { SpiAdjustmentItem, SprintSelectionItem, ViewMode } from './types'
import { WorkloadModeView } from './WorkloadModeView'
import { SelectionModeView } from './SelectionModeView'
import { generateUuid } from '../../../../../utils/uuids'

// 型のエクスポート
export type { SpiAdjustmentItem, SprintSelectionItem } from './types'

interface Props {
  open: boolean
  onClose: () => void
  onApply: (items: SpiAdjustmentItem[]) => ProductBacklogRow[] | undefined
  onValidationError: (message: string) => void
  sprints: SprintDetail[]
  deliverable: ProductBacklogItemRow
  teamUuid: string | undefined
  workloadUnit: WorkloadUnit
  teamMembers?: UserBasic[]
}

const isValidSprint = (status: SprintStatus): boolean => {
  return status === SprintStatus.STANDBY || status === SprintStatus.INPROGRESS
}

export const SprintAssignmentDialog: React.FC<Props> = ({
  open,
  onClose,
  onApply,
  onValidationError,
  sprints,
  deliverable,
  teamUuid,
  workloadUnit,
  teamMembers = [],
}) => {
  const intl = useIntl()
  // SPI単位のアイテム（工数入力モード用）
  const [spiItems, setSpiItems] = useState<SpiAdjustmentItem[]>([])
  // スプリント選択用アイテム（選択モード用）
  const [selectionItems, setSelectionItems] = useState<SprintSelectionItem[]>(
    []
  )
  const [viewMode, setViewMode] = useState<ViewMode>('workload')
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null)

  // スプリントマップ（UUID → SprintDetail）
  const sprintMap = useMemo(() => {
    const map = new Map<string, SprintDetail>()
    sprints.forEach(s => map.set(s.uuid, s))
    return map
  }, [sprints])

  // ワークロード単位の変換
  const { hoursPerSelectedUnit, convertWorkloadFromHourToSelectedUnit } =
    useWorkloadUnit(workloadUnit)

  // 選択単位から時間への変換
  const convertWorkloadFromSelectedUnitToHour = useCallback(
    (value: number) => value * hoursPerSelectedUnit,
    [hoursPerSelectedUnit]
  )

  // 単位ラベルの取得
  const workloadUnitLabel = useMemo(() => {
    switch (workloadUnit) {
      case WorkloadUnit.MONTH:
        return intl.formatMessage({ id: 'workloadUnit.month' })
      case WorkloadUnit.DAY:
        return intl.formatMessage({ id: 'workloadUnit.day' })
      case WorkloadUnit.HOUR:
      default:
        return intl.formatMessage({ id: 'workloadUnit.hour' })
    }
  }, [workloadUnit, intl])

  // ダイアログが開くたびに状態を初期化
  useEffect(() => {
    if (!open) return

    // 既存SPIからSpiAdjustmentItemを構築
    const currentSprintItems: SprintProductItemDetail[] =
      deliverable.sprintProductItems

    // スプリントのnumでソートするためのマップ
    const sprintNumMap = new Map<string, number>()
    sprints.forEach(s => sprintNumMap.set(s.uuid, s.num))

    // SPIのdisplayOrderマップを作成
    const spiDisplayOrderMap = new Map<string, number>()
    currentSprintItems.forEach(spi =>
      spiDisplayOrderMap.set(spi.uuid, spi.displayOrder ?? 0)
    )

    const spiAdjustmentItems: SpiAdjustmentItem[] = currentSprintItems
      .map(spi => {
        const sprint = sprintMap.get(spi.sprintUuid)
        return {
          uuid: spi.uuid,
          sprintUuid: spi.sprintUuid,
          sprintName: sprint?.name || '',
          startDate: sprint?.startDate || '',
          endDate: sprint?.endDate || '',
          estimatedHour: spi.estimatedHour || 0,
          assignee: spi.assignee || null,
          isNew: false,
        }
      })
      // スプリントnum順 → 同一スプリント内でdisplayOrder順
      .sort((a, b) => {
        const numA = sprintNumMap.get(a.sprintUuid) ?? 0
        const numB = sprintNumMap.get(b.sprintUuid) ?? 0
        if (numA !== numB) return numA - numB
        // 同一スプリント内ではdisplayOrder順
        const displayOrderA = spiDisplayOrderMap.get(a.uuid) ?? 0
        const displayOrderB = spiDisplayOrderMap.get(b.uuid) ?? 0
        return displayOrderA - displayOrderB
      })

    // スプリント選択用アイテムを構築
    const linkedSprintUuids = new Set(
      currentSprintItems.map(spi => spi.sprintUuid)
    )

    // チームでフィルタ、num昇順でソート
    const filteredSprints = sprints
      .filter(
        s =>
          s.teamUuid === teamUuid &&
          (linkedSprintUuids.has(s.uuid) || isValidSprint(s.status))
      )
      .sort((a, b) => a.num - b.num)

    const selectionItemList: SprintSelectionItem[] = filteredSprints.map(
      sprint => ({
        sprintUuid: sprint.uuid,
        sprintName: sprint.name,
        startDate: sprint.startDate,
        endDate: sprint.endDate,
        checked: linkedSprintUuids.has(sprint.uuid),
      })
    )

    setSpiItems(spiAdjustmentItems)
    setSelectionItems(selectionItemList)
    setViewMode('workload')
    setAddMenuAnchor(null)
  }, [open, deliverable, sprints, teamUuid, sprintMap])

  // 見積工数の合計（時間単位）
  const totalEstimatedHour = useMemo(
    () => spiItems.reduce((sum, item) => sum + item.estimatedHour, 0),
    [spiItems]
  )

  // スプリント追加メニュー
  const handleAddMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAddMenuAnchor(event.currentTarget)
    },
    []
  )

  const handleAddMenuClose = useCallback(() => {
    setAddMenuAnchor(null)
  }, [])

  // 新規SPI行を追加（スプリント選択）
  const handleAddSpi = useCallback(
    (sprintUuid: string) => {
      const sprint = sprintMap.get(sprintUuid)
      if (!sprint) return

      const newSpiItem: SpiAdjustmentItem = {
        uuid: generateUuid(),
        sprintUuid: sprint.uuid,
        sprintName: sprint.name,
        startDate: sprint.startDate,
        endDate: sprint.endDate,
        estimatedHour: 0,
        assignee: null,
        isNew: true,
      }

      setSpiItems(prev => [...prev, newSpiItem])

      // selectionItemsも更新
      setSelectionItems(prev =>
        prev.map(item =>
          item.sprintUuid === sprintUuid ? { ...item, checked: true } : item
        )
      )

      setAddMenuAnchor(null)
    },
    [sprintMap]
  )

  // SPI行削除
  const handleRemoveSpi = useCallback((spiUuid: string) => {
    setSpiItems(prev => {
      const newItems = prev.filter(item => item.uuid !== spiUuid)

      // 削除後、そのスプリントのSPIが0件ならselectionItemsも更新
      const removedItem = prev.find(item => item.uuid === spiUuid)
      if (removedItem) {
        const hasOtherSpiInSprint = newItems.some(
          item => item.sprintUuid === removedItem.sprintUuid
        )
        if (!hasOtherSpiInSprint) {
          setSelectionItems(selItems =>
            selItems.map(item =>
              item.sprintUuid === removedItem.sprintUuid
                ? { ...item, checked: false }
                : item
            )
          )
        }
      }

      return newItems
    })
  }, [])

  // 工数変更
  const handleWorkloadChange = useCallback(
    (spiUuid: string, value: string) => {
      const numValue = parseFloat(value) || 0
      const hourValue = convertWorkloadFromSelectedUnitToHour(numValue)
      setSpiItems(prev =>
        prev.map(item =>
          item.uuid === spiUuid ? { ...item, estimatedHour: hourValue } : item
        )
      )
    },
    [convertWorkloadFromSelectedUnitToHour]
  )

  // 担当者変更
  const handleAssigneeChange = useCallback(
    (spiUuid: string, assignee: UserBasic | null) => {
      setSpiItems(prev =>
        prev.map(item => (item.uuid === spiUuid ? { ...item, assignee } : item))
      )
    },
    []
  )

  // スプリント選択モードでのチェック変更
  const handleCheckChange = useCallback(
    (sprintUuid: string, checked: boolean) => {
      if (checked) {
        // チェックを入れた場合: 新規SPI行を追加
        const sprint = sprintMap.get(sprintUuid)
        if (!sprint) return

        const newSpiItem: SpiAdjustmentItem = {
          uuid: generateUuid(),
          sprintUuid: sprint.uuid,
          sprintName: sprint.name,
          startDate: sprint.startDate,
          endDate: sprint.endDate,
          estimatedHour: 0,
          assignee: null,
          isNew: true,
        }
        setSpiItems(prev => [...prev, newSpiItem])
      } else {
        // チェックを外した場合: そのスプリントの全SPI行を削除
        setSpiItems(prev => prev.filter(item => item.sprintUuid !== sprintUuid))
      }

      setSelectionItems(prev =>
        prev.map(item =>
          item.sprintUuid === sprintUuid ? { ...item, checked } : item
        )
      )
    },
    [sprintMap]
  )

  const handleApplyToGrid = useCallback(() => {
    onApply(spiItems)
    onClose()
  }, [spiItems, onApply, onClose])

  // 日付フォーマット
  const formatDate = useCallback((dateStr: string) => {
    if (!dateStr) return ''
    return dateV2Service.formatMonthAndDate(
      dateV2Service.newDateFromString(dateStr)
    )
  }, [])

  // 変更があるかどうかを検出
  const hasChanges = useMemo(() => {
    const currentSprintItems: SprintProductItemDetail[] =
      deliverable.sprintProductItems
    const originalSpiMap = new Map(
      currentSprintItems.map(spi => [spi.uuid, spi])
    )

    // 新規追加があるか
    if (spiItems.some(item => item.isNew)) return true

    // 削除があるか
    if (spiItems.length !== currentSprintItems.length) return true

    // 値の変更があるか
    return spiItems.some(item => {
      const original = originalSpiMap.get(item.uuid)
      if (!original) return true
      return (
        item.estimatedHour !== (original.estimatedHour || 0) ||
        item.assignee?.uuid !== original.assignee?.uuid
      )
    })
  }, [spiItems, deliverable.sprintProductItems])

  // 同一スプリント内で同じ担当者が重複しているかチェック
  const hasDuplicateAssignee = useMemo(() => {
    const sprintAssigneeMap = new Map<string, Set<string>>()
    for (const item of spiItems) {
      if (!item.assignee) continue
      const assigneeSet = sprintAssigneeMap.get(item.sprintUuid)
      if (assigneeSet) {
        if (assigneeSet.has(item.assignee.uuid)) {
          return true
        }
        assigneeSet.add(item.assignee.uuid)
      } else {
        sprintAssigneeMap.set(item.sprintUuid, new Set([item.assignee.uuid]))
      }
    }
    return false
  }, [spiItems])

  // 反映不可の理由
  const cannotApplyReason = useMemo(() => {
    if (!hasChanges) return null
    if (hasDuplicateAssignee) {
      return intl.formatMessage({
        id: 'productBacklog.sprintInteractiveAdjustment.error.duplicateAssignee',
      })
    }
    return null
  }, [hasChanges, hasDuplicateAssignee, intl])

  // グリッドに反映ボタンクリック時のバリデーション
  const handleApplyClick = useCallback(() => {
    if (cannotApplyReason) {
      onValidationError(cannotApplyReason)
      return
    }
    handleApplyToGrid()
  }, [cannotApplyReason, handleApplyToGrid, onValidationError])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth={true}>
      <DialogTitle>
        {intl.formatMessage({
          id: 'productBacklog.sprintInteractiveAdjustment.title',
        })}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
        {/* 固定ヘッダー部分: 2行レイアウト */}
        <Box
          sx={{
            px: 2,
            py: 1,
            flexShrink: 0,
          }}
        >
          {/* 1行目: 成果物 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{ flexShrink: 0 }}
            >
              {intl.formatMessage({
                id: 'productBacklog.sprintInteractiveAdjustment.deliverable',
              })}
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold', color: '#000' }}
              noWrap={true}
              title={deliverable.wbsItem.displayName || '-'}
            >
              {deliverable.wbsItem.displayName || '-'}
            </Typography>
          </Box>
          {/* 2行目: 担当者 + 見積合計 */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="body1" color="textSecondary">
              {intl.formatMessage({
                id: 'productBacklog.sprintInteractiveAdjustment.assignee',
              })}
            </Typography>
            {deliverable.wbsItem.responsible ? (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Avatar
                  src={deliverable.wbsItem.responsible.iconUrl}
                  sx={{ width: 24, height: 24 }}
                />
                <Typography variant="h6" noWrap={true} sx={{ color: '#000' }}>
                  {deliverable.wbsItem.responsible.name}
                </Typography>
              </Box>
            ) : (
              <Typography variant="h6" sx={{ color: '#000' }}>
                -
              </Typography>
            )}
            {/* 元の見積と見積合計（右寄せ、上下配置） */}
            <Box
              sx={{
                ml: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 0.5,
              }}
            >
              {/* 元の見積 */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  {intl.formatMessage({
                    id: 'productBacklog.sprintInteractiveAdjustment.originalEstimate',
                  })}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: '#000', minWidth: 60, textAlign: 'right' }}
                >
                  {convertWorkloadFromHourToSelectedUnit(
                    deliverable.wbsItem.estimatedWorkload?.hour || 0
                  )}{' '}
                  {workloadUnitLabel}
                </Typography>
              </Box>
              {/* 見積合計 */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  {intl.formatMessage({
                    id: 'productBacklog.sprintInteractiveAdjustment.total',
                  })}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: '#000', minWidth: 60, textAlign: 'right' }}
                >
                  {convertWorkloadFromHourToSelectedUnit(totalEstimatedHour)}{' '}
                  {workloadUnitLabel}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* モードに応じた表示切り替え */}
        {viewMode === 'workload' ? (
          <WorkloadModeView
            spiItems={spiItems}
            selectionItems={selectionItems}
            workloadUnitLabel={workloadUnitLabel}
            addMenuAnchor={addMenuAnchor}
            onAddMenuOpen={handleAddMenuOpen}
            onAddMenuClose={handleAddMenuClose}
            onAddSpi={handleAddSpi}
            onRemoveSpi={handleRemoveSpi}
            onWorkloadChange={handleWorkloadChange}
            onAssigneeChange={handleAssigneeChange}
            onSwitchToSelection={() => setViewMode('selection')}
            formatDate={formatDate}
            convertWorkloadFromHourToSelectedUnit={
              convertWorkloadFromHourToSelectedUnit
            }
          />
        ) : (
          <SelectionModeView
            items={selectionItems}
            onCheckChange={handleCheckChange}
            formatDate={formatDate}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {intl.formatMessage({ id: 'dialog.cancel' })}
        </Button>
        {viewMode === 'workload' ? (
          <Button
            onClick={handleApplyClick}
            variant="contained"
            color="primary"
            disabled={!hasChanges}
            sx={{ px: 1.5 }}
          >
            {intl.formatMessage({
              id: 'productBacklog.sprintInteractiveAdjustment.apply',
            })}
          </Button>
        ) : (
          <Button
            onClick={() => setViewMode('workload')}
            variant="contained"
            color="primary"
            sx={{ px: 1.5 }}
          >
            {intl.formatMessage({
              id: 'productBacklog.sprintInteractiveAdjustment.backToWorkload',
            })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
